// Shared faceswap config contract for the admin content editor.
//
// Field names here are a HARD front/back-end contract — the backend reads
// `config.faceswap.{engine,restore_model,restore_visibility,codeformer_weight,
// face_boost,face_model}` (snake_case) when resolving a series' faceswap tier.
// See openspec change `configurable-faceswap-fidelity`, specs/faceswap-config.

export type FaceSwapEngine =
  | 'reactor'
  | 'reactor_multi'
  | 'pulid_reactor'
  | 'jimeng';

export type FaceSwapRestoreModel =
  | 'none'
  | 'GFPGANv1.4.pth'
  | 'GFPGANv1.3.pth'
  | 'codeformer-v0.1.0.pth'
  | 'GPEN-BFR-512.onnx'
  | 'RestoreFormer_PP.onnx';

export type FaceSwapFaceModel = 'single' | 'multi';

/**
 * Optional faceswap block stored under a content package's `config.faceswap`.
 * Every field is optional — a missing field falls back to the platform default
 * server-side (field-level three-layer resolution), so existing packages with
 * no `faceswap` key must not error.
 */
export interface FaceSwapConfig {
  engine?: FaceSwapEngine;
  restore_model?: FaceSwapRestoreModel;
  restore_visibility?: number; // [0,1]
  codeformer_weight?: number; // [0,1], only when restore_model === codeformer
  face_boost?: boolean;
  face_model?: FaceSwapFaceModel;
}

/** The restore_model value that gates codeformer_weight. */
export const CODEFORMER_MODEL: FaceSwapRestoreModel = 'codeformer-v0.1.0.pth';

export const ENGINE_OPTIONS: Array<{ value: FaceSwapEngine; label: string; desc: string }> = [
  { value: 'reactor', label: '基础换脸（快/省）', desc: '纯 ReActor 换脸到底版，~8s 近零 GPU 成本。正脸底版效果可接受，量产首选。' },
  { value: 'reactor_multi', label: '多图换脸（更像）', desc: '用户传 2-4 张脸合成稳定身份再换脸，比单图更像本人。成本仍低。' },
  { value: 'pulid_reactor', label: 'PuLID 重绘（最像/慢）', desc: 'PuLID 身份注入扩散重绘 + 末端精修，最像本人、能修大角度。跑扩散，慢且贵，适合高价套系。' },
  { value: 'jimeng', label: '即梦人物参考', desc: '走即梦 API 人物参考，亚洲脸友好、不占自有 GPU。单图即较好保真。' },
];

export const RESTORE_MODEL_OPTIONS: Array<{
  value: FaceSwapRestoreModel;
  label: string;
  desc: string;
}> = [
  { value: 'none', label: '不修复 (none)', desc: '不做人脸修复，最保留换脸原貌，但可能粗糙/有瑕疵。' },
  { value: 'GFPGANv1.4.pth', label: 'GFPGAN v1.4', desc: '通用人脸修复，速度快。修复力强但偏「磨皮」，易塑料感。' },
  { value: 'GFPGANv1.3.pth', label: 'GFPGAN v1.3', desc: 'GFPGAN 老版本，效果与 v1.4 接近，一般用 v1.4。' },
  { value: 'codeformer-v0.1.0.pth', label: 'CodeFormer v0.1.0', desc: '通常比 GFPGAN 更自然、塑料感更轻，可调保真权重。推荐优先试。' },
  { value: 'GPEN-BFR-512.onnx', label: 'GPEN-BFR-512', desc: '512 分辨率修复，细节锐利，适合高清需求。' },
  { value: 'RestoreFormer_PP.onnx', label: 'RestoreFormer++', desc: '基于 Transformer 的修复，对侧脸/遮挡相对稳健。' },
];

export const FACE_MODEL_OPTIONS: Array<{
  value: FaceSwapFaceModel;
  label: string;
  desc: string;
}> = [
  { value: 'single', label: '单图', desc: '用一张人脸换脸（默认）。最省，正脸效果可接受。' },
  { value: 'multi', label: '多图', desc: '用 2-4 张脸合成更稳的身份特征，更像本人。需多图上传支持。' },
];

/**
 * Suggested minimum price (in cents) per engine, used only for the soft cost
 * hint. Tunable here without touching component code. High-compute engines
 * (pulid_reactor / reactor_multi) warrant a higher floor; the hint never
 * blocks save — operators still set the final price by hand.
 */
export const ENGINE_MIN_PRICE_CENTS: Record<FaceSwapEngine, number> = {
  reactor: 2990,
  reactor_multi: 3990,
  pulid_reactor: 5990,
  jimeng: 3990,
};

/** Engines that trigger the soft cost hint when price is below the floor. */
export const COST_HINT_ENGINES: FaceSwapEngine[] = [
  'pulid_reactor',
  'reactor_multi',
];
